import React from 'react'
import ReactGA from 'react-ga4'
import queryString from 'query-string'
import { color } from 'console-log-colors'

import { ID, env } from '~/service'
import { findRouteMatchingLocation } from '~/pages/routes'
import { logFactory } from '~/util'

interface AnalyticsOptions {
  testMode?: boolean
  gtagOptions?: Record<string, unknown>
}

const logger = logFactory('Analytics', color.bold.greenBright)

// Reported until the API returns property manager details with the community config.
export const PROPERTY_MANAGER_NOT_AVAILABLE = {
  id: '0000',
  name: 'Not Available',
}

// Top level event catagories.
export const ANALYTICS_CATEGORIES = {
  MAP: 'Map',
  // Events for contacting the community or leasing agent.
  CONTACT: 'Contact',
  // Events for interacting with videos or pictures.
  MEDIA: 'Media',
  // Events for interacting with checkIn page.
  CHECKIN: 'CheckIn',
  // Events for opening community documents such as the fee sheet.
  DOCUMENT: 'Document',
  // Events for interacting with a unit, such as favoriting it.
  UNIT: 'Unit',
  // Events for unlocking a unit's door during a self tour.
  LOCK: 'Lock',
  // Events for starting a reservation or application.
  LEASING: 'Leasing',
}

export interface IClient {
  initialize: (trackingId: string, options?: AnalyticsOptions) => void
  set: (fieldsObject: Record<string, string>) => void
  event: (name: string, params?: Record<string, unknown>) => void
  send: (args: Record<string, string>) => void
}

/**
 * Convert a human readable action ("Call Community", "verification-start")
 * into a GA4 event name: lowercase letters, numbers and underscores, max 40 characters.
 */
export function toEventName(action: string) {
  return action
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
}

/**
 * A service for tracking page and user events to our analytics backend.
 */
export class Analytics {
  initialized = false
  client: IClient
  // Community and property manager IDs and names, sent with every page view.
  communityDimensions: Record<string, string> = {}
  // The custom dimensions of the current page, attached to every event on it.
  pageDimensions: Record<string, string> = {}
  // The last page URL reported to GA. Some clicks change the URL without
  // leaving the page (e.g. an expired quote links to "#"). Without this check
  // each one would be counted as another visit to the same page.
  lastTrackedUrl = ''

  constructor(
    trackingId: ID = env.gaTrackingId,
    options: AnalyticsOptions = {
      // Only built, deployed sites report to GA; local dev servers do not.
      testMode: env.test || env.devServer,
      // verbose: env.verbose,
    },
    client: IClient = ReactGA
  ) {
    this.client = client
    logger.info('init:', trackingId, options)
    // this.options = options
    // Initialize the tracking id. trackPage() sends every page view,
    // so disable the automatic one gtag sends on initialization.
    this.client.initialize(String(trackingId), {
      ...options,
      gtagOptions: { send_page_view: false },
    })
  }

  /**
   * Associate a community and its property manager with the current session.
   */
  setCommunity(
    communityId: ID,
    communityName: string,
    propertyManagerId?: ID,
    propertyManagerName?: string
  ) {
    logger.info(
      'set community:',
      communityId,
      communityName,
      propertyManagerId,
      propertyManagerName
    )
    // IDs and names are sent separately so reports can group by the stable
    // ID even when a name changes.
    const hasPropertyManager = !!(propertyManagerId && propertyManagerName)
    this.communityDimensions = {
      community_id: String(communityId),
      community_name: communityName,
      property_manager_id: hasPropertyManager
        ? String(propertyManagerId)
        : PROPERTY_MANAGER_NOT_AVAILABLE.id,
      property_manager_name: hasPropertyManager
        ? propertyManagerName!
        : PROPERTY_MANAGER_NOT_AVAILABLE.name,
    }
    this.client.set(this.communityDimensions)
  }

  /**
   * Get the search parameters from the URL, filtering
   * any parameters that are not relevant to analytics tracking.
   * @param The location object from React Router or Window.
   */
  getQuery(location: { search: string } = window.location) {
    const query = queryString.parse(location.search)

    // Strip query parameters that aren't relavent to analytics.
    ;['from', 'previousTitle'].forEach((n) => {
      delete query[n]
    })

    const out = queryString.stringify(query)
    return out ? `?${out}` : ''
  }

  /**
   * Determine the URL of the current page.
   * @param The location object from React Router or Window.
   */
  getPage(location: { search: string; pathname: string }) {
    // Get query parameters since they are not returned by the route matching.
    const query = this.getQuery(location)

    const routeResult = findRouteMatchingLocation(location.pathname)

    if (!routeResult) {
      return { url: location.pathname + query, guestCardUuid: '' }
    } else {
      const {
        match: { url, params },
      } = routeResult

      // Construct a pathname without the guest card UUID.
      const path = params.uuid
        ? // Exclude the UUID so we can track page views
          // regardless of the guest card they are associated
          // with. The UUID is tracked as a custom dimension instead.
          url.replace(params.uuid, '').replaceAll('//', '/')
        : url

      return { url: path + query, guestCardUuid: params.uuid || '' }
    }
  }

  /**
   * Track the current page.
   * @param {object} [location] - The location object from React Router or Window.
   */
  trackPage(location: { search: string; pathname: string }) {
    const { url, guestCardUuid } = this.getPage(location)
    if (url === this.lastTrackedUrl) return
    this.lastTrackedUrl = url

    // Sent as event parameters; each is registered as a custom dimension in GA.
    const dimensions = {
      guest_card_uuid: guestCardUuid,
      ...this.communityDimensions,
    }
    this.pageDimensions = dimensions

    logger.info(
      [
        ['track page:', color.white],
        [url, color.green],
      ],
      'dimensions:',
      { dimensions }
    )

    // GA4 reports pages by page_location, which otherwise defaults to the
    // browser URL and would include the guest card UUID.
    const pageLocation = window.location.origin + url

    // Indicate what page subsequent events are associated with.
    this.client.set({
      page: url,
      page_location: pageLocation,
      ...dimensions,
    })
    // Track the current page load.
    this.client.send({
      hitType: 'pageview',
      page: url,
      location: pageLocation,
      ...dimensions,
    })
  }

  /**
   * Track a non-pageview event/interaction.
   * @see https://github.com/react-ga/react-ga#reactgaeventargs
   * @param Whether this event was triggered by the user.
   */
  trackEvent(
    category: string,
    action: string,
    label?: string,
    value?: number,
    userInteraction = true
  ) {
    // Requiring category, action AND label so we can ensure consistent tracking data.
    if (category && action && label) {
      logger.info(
        'event:',
        `category(${category})`,
        `action(${action})`,
        `label(${label})`,
        `value(${value})`,
        `userInteraction(${userInteraction})`
      )
      this.client.event(toEventName(action), {
        event_category: category,
        event_label: label,
        value,
        non_interaction: !userInteraction,
        ...this.pageDimensions,
      })
    }
  }

  /**
   * Track a link to an external site.
   * https://github.com/react-ga/react-ga#reactgaoutboundlinkargs-hitcallback
   */
  trackExternalLink(url: string) {
    logger.info('track external link:', url)
    this.client.event(toEventName('Open External Link'), {
      event_category: 'External Link',
      event_label: url,
      ...this.pageDimensions,
    })
  }
}

export const AnalyticsContext = React.createContext<null | Analytics>(null)
export const AnalyticsProvider = AnalyticsContext.Provider

export function useAnalyticsClient() {
  const analytics = React.useContext(AnalyticsContext)
  if (!analytics) {
    console.error(
      'Analytics client not found in context! Wrap your component in `<AnalyticsProvider>` before using `useAnalytics()`.'
    )
    // return null
    // throw new Error(
    //   'Analytics client not found in context! Wrap your component in `<AnalyticsProvider>` before using `useAnalytics()`.'
    // )
  }
  return analytics!
}
